/**
 * Recycle bin entry point for the orders tracker header.
 *
 * Sits in the same right-cluster as the view toggle, the LPO email button
 * and Export, and matches their styling exactly (outline / md, icon only,
 * aria-label + title), so it reads as one of that set rather than an
 * afterthought bolted on.
 *
 * ADMIN ONLY, and not merely hidden: the orders SELECT policy already
 * refuses binned rows to every non-admin, so a podiatrist who reached this
 * component would see a count of zero regardless. The isAdmin check is
 * there so the control does not appear at all, which is a UI concern; the
 * security is in the policy.
 *
 * NO NEW QUERY PATH. The count comes from the page's own
 * orderQueries.ordersListQuery with the `deletedOnly` flag that already
 * shipped for the bin page itself: same SDK function, same builder, same
 * select string, same RLS policy. pageSize 1 because only `total` is read,
 * so the response carries one row rather than the bin.
 */
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import { Button } from '@insolech/ui';
import { orderQueries } from '@insolech/sdk';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/features/auth/AuthContext';

export const RECYCLE_BIN_PATH = '/admin/orders/bin';

export function RecycleBinButton(): React.ReactElement | null {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();

  const binQuery = useQuery({
    ...orderQueries.ordersListQuery(supabase, {
      status: 'total',
      deletedOnly: true,
      page: 0,
      // Only `total` is read; there is no need to ship the bin itself.
      pageSize: 1,
    }),
    enabled: isAdmin,
    placeholderData: keepPreviousData,
  });

  if (!isAdmin) return null;

  const count = binQuery.data?.total ?? 0;
  // Empty bin keeps the control but carries no count: the way in must not
  // disappear just because there is nothing in there right now.
  const label = count > 0 ? `Recycle bin (${count})` : 'Recycle bin';

  return (
    <Button
      variant="outline"
      size="md"
      onClick={() => navigate(RECYCLE_BIN_PATH)}
      aria-label={label}
      title={label}
      data-testid="recycle-bin-button"
    >
      <Trash2 className="h-4 w-4" />
      {count > 0 ? (
        <span
          className="ms-1.5 inline-flex min-w-[1.25rem] items-center justify-center rounded-full bg-warning-100 px-1 text-[11px] font-medium text-warning-800"
          data-testid="recycle-bin-count"
        >
          {count}
        </span>
      ) : null}
    </Button>
  );
}
